"use client";

import { type KeyboardEvent, useState } from "react";
import { Checkbox } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
} from "@/components/tool-kit/styles";
import type { SUDOKU_GENERATOR_STRINGS } from "@/components/tools/sudoku-generator";
import { t } from "@/lib/i18n/interpolate";
import { colOf, peersOf, rowOf } from "@/lib/tool-kit/games-organizers/sudoku";

// biome-ignore-start lint/suspicious/noBitwiseOperators: pencil notes are a bit mask, one bit per digit
type Strings = typeof SUDOKU_GENERATOR_STRINGS;

interface Snapshot {
  entries: number[];
  notes: number[];
}

const DIGITS = [1, 2, 3, 4, 5, 6, 7, 8, 9];
const ARROWS: Record<string, [number, number]> = {
  ArrowUp: [-1, 0],
  ArrowDown: [1, 0],
  ArrowLeft: [0, -1],
  ArrowRight: [0, 1],
};

function blank(): number[] {
  return new Array<number>(81).fill(0);
}

const RIGHT_EDGE = ["border-r", "border-r", "border-r-2 border-r-foreground"];
const BOTTOM_EDGE = ["border-b", "border-b", "border-b-2 border-b-foreground"];

function cellBorders(cell: number): string {
  const c = colOf(cell);
  const r = rowOf(cell);
  const right = c === 8 ? "" : RIGHT_EDGE[c % 3];
  const bottom = r === 8 ? "" : BOTTOM_EDGE[r % 3];
  return `${right} ${bottom}`;
}

function describeCell(
  strings: Strings,
  cell: number,
  digit: number,
  given: boolean,
  wrong: boolean,
  notes: number
): string {
  const where = t(strings.cellLabel, {
    row: rowOf(cell) + 1,
    col: colOf(cell) + 1,
  });
  let what: string = strings.cellEmpty;
  if (digit) {
    what = given
      ? t(strings.cellGiven, { digit })
      : t(strings.cellEntered, { digit });
  } else if (notes) {
    what = t(strings.cellNotes, {
      digits: DIGITS.filter((d) => notes & (1 << (d - 1))).join(" "),
    });
  }
  return `${where}: ${what}${wrong ? `, ${strings.cellWrong}` : ""}`;
}

export default function SudokuBoard({
  puzzle,
  solution,
  strings,
}: {
  puzzle: readonly number[];
  solution: readonly number[];
  strings: Strings;
}) {
  const [entries, setEntries] = useState<number[]>(blank);
  const [notes, setNotes] = useState<number[]>(blank);
  const [history, setHistory] = useState<Snapshot[]>([]);
  const [selected, setSelected] = useState(40);
  const [noteMode, setNoteMode] = useState(false);
  const [showMistakes, setShowMistakes] = useState(false);

  const value = (cell: number) => puzzle[cell] || entries[cell] || 0;
  const isGiven = (cell: number) => (puzzle[cell] ?? 0) !== 0;
  const solved = solution.every((digit, cell) => value(cell) === digit);

  function commit(nextEntries: number[], nextNotes: number[]) {
    setHistory((past) => [...past.slice(-199), { entries, notes }]);
    setEntries(nextEntries);
    setNotes(nextNotes);
  }

  function enter(digit: number) {
    if (isGiven(selected)) {
      return;
    }
    const nextEntries = entries.slice();
    const nextNotes = notes.slice();
    if (noteMode && digit) {
      if (!entries[selected]) {
        nextNotes[selected] = (nextNotes[selected] ?? 0) ^ (1 << (digit - 1));
      }
    } else {
      nextEntries[selected] = digit;
      nextNotes[selected] = 0;
      if (digit) {
        for (const peer of peersOf(selected)) {
          nextNotes[peer] = (nextNotes[peer] ?? 0) & ~(1 << (digit - 1));
        }
      }
    }
    commit(nextEntries, nextNotes);
  }

  function move(dRow: number, dCol: number) {
    const row = Math.min(8, Math.max(0, rowOf(selected) + dRow));
    const col = Math.min(8, Math.max(0, colOf(selected) + dCol));
    setSelected(row * 9 + col);
  }

  function onKeyDown(event: KeyboardEvent) {
    const arrow = ARROWS[event.key];
    if (arrow) {
      event.preventDefault();
      move(arrow[0], arrow[1]);
    } else if (event.key >= "1" && event.key <= "9") {
      event.preventDefault();
      enter(Number(event.key));
    } else if (["0", "Backspace", "Delete"].includes(event.key)) {
      event.preventDefault();
      enter(0);
    } else if (event.key === "n" || event.key === "N") {
      setNoteMode((on) => !on);
    }
  }

  function undo() {
    const last = history.at(-1);
    if (last) {
      setEntries(last.entries);
      setNotes(last.notes);
      setHistory(history.slice(0, -1));
    }
  }

  function hint() {
    const target =
      isGiven(selected) || value(selected) === solution[selected]
        ? solution.findIndex((digit, cell) => value(cell) !== digit)
        : selected;
    if (target >= 0) {
      const nextEntries = entries.slice();
      nextEntries[target] = solution[target] ?? 0;
      setSelected(target);
      commit(nextEntries, notes);
    }
  }

  function reset() {
    commit(blank(), blank());
  }

  const selDigit = value(selected);
  return (
    <div className="space-y-4 print:hidden">
      <fieldset
        aria-label={strings.boardLabel}
        className="m-0 mx-auto grid aspect-square w-full min-w-0 max-w-md grid-cols-9 overflow-hidden rounded-lg border-2 border-foreground bg-card p-0"
      >
        {puzzle.map((_, cell) => {
          const digit = value(cell);
          const wrong =
            showMistakes &&
            !isGiven(cell) &&
            digit !== 0 &&
            digit !== solution[cell];
          const related = peersOf(selected).includes(cell) && cell !== selected;
          let tone = "bg-card";
          if (cell === selected) {
            tone = "bg-primary/25";
          } else if (selDigit && digit === selDigit) {
            tone = "bg-primary/15";
          } else if (related) {
            tone = "bg-muted/60";
          }
          const cellNotes = notes[cell] ?? 0;
          let text = "text-primary";
          if (isGiven(cell)) {
            text = "font-bold text-foreground";
          } else if (wrong) {
            text = "text-destructive";
          }
          return (
            <button
              aria-label={describeCell(
                strings,
                cell,
                digit,
                isGiven(cell),
                wrong,
                cellNotes
              )}
              aria-pressed={cell === selected}
              className={`relative flex items-center justify-center text-lg sm:text-2xl ${cellBorders(cell)} border-border ${tone} ${text} focus:outline-none focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-primary`}
              key={`${rowOf(cell)}-${colOf(cell)}`}
              onClick={() => setSelected(cell)}
              onKeyDown={onKeyDown}
              tabIndex={cell === selected ? 0 : -1}
              type="button"
            >
              {digit ? (
                digit
              ) : (
                <span className="grid h-full w-full grid-cols-3 text-[0.55rem] text-muted-foreground leading-none sm:text-[0.65rem]">
                  {DIGITS.map((d) => (
                    <span className="flex items-center justify-center" key={d}>
                      {cellNotes & (1 << (d - 1)) ? d : ""}
                    </span>
                  ))}
                </span>
              )}
            </button>
          );
        })}
      </fieldset>

      <div aria-live="polite" className="text-center font-medium text-sm">
        {solved ? strings.solved : ""}
      </div>

      <div className="mx-auto grid max-w-md grid-cols-9 gap-1">
        {DIGITS.map((d) => (
          <button
            aria-label={t(strings.enterDigit, { digit: d })}
            className={BUTTON_SMALL}
            key={d}
            onClick={() => enter(d)}
            type="button"
          >
            {d}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap justify-center gap-2">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => enter(0)}
          type="button"
        >
          {strings.erase}
        </button>
        <button
          aria-pressed={noteMode}
          className={`${BUTTON_SECONDARY} ${noteMode ? "border-primary bg-primary/10" : ""}`}
          onClick={() => setNoteMode((on) => !on)}
          type="button"
        >
          {noteMode ? strings.notesOn : strings.notesOff}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={history.length === 0}
          onClick={undo}
          type="button"
        >
          {strings.undo}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={solved}
          onClick={hint}
          type="button"
        >
          {strings.hint}
        </button>
        <button className={BUTTON_SECONDARY} onClick={reset} type="button">
          {strings.clearEntries}
        </button>
      </div>
      <div className="flex justify-center">
        <Checkbox
          checked={showMistakes}
          label={strings.showMistakes}
          onChange={setShowMistakes}
        />
      </div>
      <p className={`${HINT} text-center`}>{strings.keyboardHint}</p>
    </div>
  );
}
// biome-ignore-end lint/suspicious/noBitwiseOperators: end of the board
